import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import Moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import Slider, { CustomArrowProps, Settings } from 'react-slick';
import InlineSpinner from '../../../common/components/misc/InlineSpinner';
import { CardDetailsDTO } from '../../../common/types/dto/CardDetailsDTO';
import { TranslationFuncProp } from '../../../common/types/prop-types/common';
import cssForms from '../../assets/scss/Forms.scss';
import CardsContainer, { CardsProps } from '../../valueandcare/components/containers/CardsContainer';
import css from './CardQuickSelection.scss';

const LoadingCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    if (!props.cards) {
        return (
            <div className={classNames([css.card, css.loadingCard])}>
                <div>
                    <InlineSpinner size={'100px'} />
                    <h4 id="loading-card-text">{props.t('loadingText')}</h4>
                </div>
            </div>
        );
    }
    return null;
};

const isValidCard = (card: CardDetailsDTO) => {
    return card.number !== undefined && card.number !== null;
};

const anyValidCard = (cards: CardDetailsDTO[]) => {
    return cards.some((x) => isValidCard(x));
};

const NoCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    if (props.cards && (props.cards.length === 0 || !anyValidCard(props.cards))) {
        return (
            <div className={css.noCard}>
                <div className={classNames(['cell', 'small-12', 'text-center', 'align-self-middle'])}>
                    <h3>{props.t('cards:layout.noCards')}</h3>
                    <p>{props.t(`cards:customerInfo.noCardsSub`)}</p>
                </div>
            </div>
        );
    }
    return null;
};

const QuickCardTemplate: React.FC<CardDetailsDTO & TranslationFuncProp> = (props) => {
    return (
        <div className={css.card}>
            <h4 className={css.cardTitle}>{props.t('cards:baseHeader')}</h4>
            <div className={css.qrcode}>{props.number ? (<QRCode value={props.number.toString()} size={128} />) : (<span>-</span>)}</div>
            <div className={css.cardDetails}>
                <p>{props.number ? props.number : '-'}</p>
                <p className={cssForms.inputUppercase}>{props.licensePlate ? props.licensePlate : '-'}</p>
                <p>{props.dealer ? props.dealer : '-'}</p>
                <p>{props.validDate ? Moment(props.validDate).format('DD/MM/YYYY') : '–'}</p>
            </div>
        </div>
    );
};

const buildCards = (cards: CardDetailsDTO[], t: TFunction) => {
    return cards.filter((x) => isValidCard(x)).map((cardItem: CardDetailsDTO, i) => (
        <React.Fragment key={i}>
            <QuickCardTemplate {...cardItem} t={t} />
        </React.Fragment>
    ));
};

const PrevArrow = (props: CustomArrowProps): JSX.Element => {
    const { onClick } = props;
    return (
        <button className={css.miniArrowSlickPrev} onClick={onClick}><FontAwesomeIcon icon={faChevronLeft} /></button>
    );
};

const NextArrow = (props: CustomArrowProps): JSX.Element => {
    const { onClick } = props;
    return (
        <button className={css.miniArrowSlickNext} onClick={onClick}><FontAwesomeIcon icon={faChevronRight} /></button>
    );
};

const calcCardWidth = () => {
    const w = window.innerWidth - (20 * 2); // full width with 20px margins
    return Math.min(640, w); // the maximum width is 640px;
};

const DisplayCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    const [cardWidth, setCardWidth] = React.useState(calcCardWidth());
    React.useEffect(() => {
        const handleResize = () => { setCardWidth(calcCardWidth()); };
        window.addEventListener('resize', handleResize); // calculate the card's width upon window resize
        return () => { window.removeEventListener('resize', handleResize); }; // cleanup
    });

    if (props.cards && props.cards.length > 0 && anyValidCard(props.cards)) {
        const settings: Settings = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
        };
        return (
            <div className={css.carouselOutline}>
                <div className={css.carouselParent} style={{ width: `${cardWidth}px` }}>
                    {props.cards.length > 1 ?
                        <Slider {...settings}>
                            {buildCards(props.cards, props.t)}
                        </Slider>
                        :
                        buildCards(props.cards, props.t)
                    }
                </div>
            </div>
        );
    }
    return null;
};

const CardSelection: React.FC<{}> = () => {
    const { t } = useTranslation();
    return (
        <CardsContainer t={t}>
            {(props: CardsProps) => {
                return (
                    <React.Fragment>
                        <LoadingCards
                            t={t}
                            {...props}
                        />
                        <NoCards
                            t={t}
                            {...props}
                        />
                        <DisplayCards
                            t={t}
                            {...props}
                        />
                    </React.Fragment>
                );
            }}
        </CardsContainer>
    );
};

export default CardSelection;
